import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

export const fleetCustodyEventKindV1Schema = z.enum([
  "assigned",
  "usage_recorded",
  "released",
])

export const fleetCustodyRecordV1Schema = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    assetId: entityIdSchema,
    assignmentId: entityIdSchema,
    custodyEventId: entityIdSchema,
    assetRevisionId: entityIdSchema,
    statusEventId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    expectedVersion: z.number().int().positive(),
    eventKind: fleetCustodyEventKindV1Schema,
    workerId: entityIdSchema.nullable(),
    crewId: entityIdSchema.nullable(),
    visitId: entityIdSchema.nullable(),
    dispatchVersion: z.number().int().positive().nullable(),
    meterMilli: z.number().int().nonnegative(),
    fuelMilli: z.number().int().nonnegative().nullable(),
    usageMilli: z.number().int().nonnegative().nullable(),
    effectiveAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(3).max(256),
  })
  .strict()
  .superRefine((value, context) => {
    if ((value.workerId === null) === (value.crewId === null)) {
      context.addIssue({
        code: "custom",
        message: "Custody requires exactly one Worker or crew.",
      })
    }
    if (
      value.eventKind === "usage_recorded" &&
      (value.usageMilli === null || value.usageMilli === 0)
    ) {
      context.addIssue({
        code: "custom",
        message: "Usage capture requires a positive usage quantity.",
      })
    }
  })

export const fleetCustodyResultV1Schema = z
  .object({
    entityType: z.literal("fleet_asset"),
    entityId: entityIdSchema,
    state: z.enum(["assigned", "released"]),
    version: z.number().int().positive(),
    assignmentId: entityIdSchema,
    assignmentVersion: z.number().int().positive(),
    eventKind: fleetCustodyEventKindV1Schema,
    workerId: entityIdSchema.nullable(),
    crewId: entityIdSchema.nullable(),
    visitId: entityIdSchema.nullable(),
    branchId: branchIdSchema,
    meterMilli: z.number().int().nonnegative(),
    fuelMilli: z.number().int().nonnegative().nullable(),
    usageMilli: z.number().int().nonnegative().nullable(),
    payrollTimeCreated: z.literal(false),
    maintenanceStatus: z.enum(["current", "maintenance_due"]).default("current"),
  })
  .strict()

export type FleetCustodyRecordV1 = z.infer<typeof fleetCustodyRecordV1Schema>
export type FleetCustodyResultV1 = z.infer<typeof fleetCustodyResultV1Schema>
